/*
* This code is the intellectual property of Bulb Inc. and other software publishers;
* it may not be altered, copied or disclosed without prior written approval.
* Copyright 2022, Bulb Inc. For more information, please email contact@hellobulb.com.
*/

import {
  Component,
  Input,
  OnInit,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  Inject,
} from '@angular/core';
import { TranslocoService, TRANSLOCO_SCOPE } from '@jsverse/transloco';
import { Subscription } from 'rxjs';
import { BulbPageViewFooterTranslationProvider } from '../translation-provider';

type LikesTranslation = {
  singular: string,
  plural: string,
};

@Component({
  selector: 'bulb-page-view-likes',
  templateUrl: './page-view-likes.component.html',
  styleUrls: ['./page-view-likes.component.scss'],
  providers: [
    BulbPageViewFooterTranslationProvider,
  ],
  standalone: false,
})
export class PageViewLikesComponent implements OnInit, OnChanges, OnDestroy {
  /**
   * The Likes the current unit has
   */
  @Input() likeCount: number;

  /**
   * Determines whether or not the current user has liked the current unit
   */
  @Input() likedByUser: boolean;

  /**
  * Icon Key for the svg-icon component to render
  */
  public iconKey: string;

  /**
   * Object containing the names of the animations
   */
  public animationNames: {
    background: string,
    secondaryBackground: string,
    text: string,
    icon: string,
  };

  /**
   * Variable holding the translation to be displayed in the template
   */
  public textTranslation: string;

  /**
   * Private variable to hold the returned translations object
   */
  private translationObject: LikesTranslation;

  /**
   * Variable to hold the subscription for the translation call from transloco
   */
  private translationObservable: Subscription;

  constructor(private transloco: TranslocoService, @Inject(TRANSLOCO_SCOPE) private scope) {}

  ngOnInit(): void {
    this.translationObservable = this.transloco.selectTranslateObject('likes', {}, this.scope)
      .subscribe((result: LikesTranslation) => {
        this.translationObject = result;
        this.setTranslationKeys(this.likeCount);
      });
    this.iconKey = this.likeCount > 0 ? 'heart-solid' : 'heart';
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.setTranslationKeys(changes.likeCount.currentValue);

    this.setAnimationNames(changes.likeCount.currentValue, changes.likeCount.previousValue);

    this.iconKey = changes.likeCount.currentValue > 0 ? 'heart-solid' : 'heart';
  }

  private setAnimationNames(currentLikes: number, previousLikes: number): void {
    if (currentLikes > previousLikes) {
      this.animationNames = {
        background: 'circle_outline',
        secondaryBackground: 'circle_starburst',
        text: 'increment_number',
        icon: 'heart_fill',
      };
    } if (currentLikes < previousLikes) {
      this.animationNames = {
        background: '',
        secondaryBackground: '',
        text: 'decrement_number',
        icon: 'heart_empty',
      };
    }
  }

  private setTranslationKeys(likeCount: number): void {
    if (likeCount === 1) {
      this.textTranslation = this.translationObject?.singular;
    } else {
      this.textTranslation = this.translationObject?.plural;
    }
  }

  ngOnDestroy(): void {
    this.translationObservable.unsubscribe();
  }
}
